/**
 * 入力スキーマ（zod）。API のリクエスト検証とフロントエンドのフォーム検証で共有する。
 * 「同じ定義を2か所に書かない」ことで、画面とAPIの受入条件がずれるのを防ぐ。
 */
import { z } from 'zod';
import { HOURLY_LEAVE_DAILY_LIMIT_HOURS } from './constants.js';
import { MAX_TIME_MINUTES } from './time.js';
import { DAY_TYPES, HOLIDAY_WORK_TREATMENTS, WORK_TYPES } from './types.js';
/** YYYY-MM-DD */
export const dateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '日付は YYYY-MM-DD 形式で指定してください');
/** YYYY-MM */
export const monthSchema = z.string().regex(/^\d{4}-\d{2}$/, '月は YYYY-MM 形式で指定してください');
/** 時刻（分）。跨日は 1440 以上の値で表す */
export const timeMinutesSchema = z
    .number()
    .int('時刻は分単位の整数で指定してください')
    .min(0)
    .max(MAX_TIME_MINUTES)
    .nullable();
export const dailyInputSchema = z.object({
    date: dateSchema,
    dayType: z.enum(DAY_TYPES),
    workType: z.enum(WORK_TYPES),
    directStart: timeMinutesSchema,
    officeIn: timeMinutesSchema,
    workStart: timeMinutesSchema,
    workEnd: timeMinutesSchema,
    officeOut: timeMinutesSchema,
    directHome: timeMinutesSchema,
    breakTotalMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .nullable(),
    hourlyLeaveHours: z.number().int().min(0).max(HOURLY_LEAVE_DAILY_LIMIT_HOURS),
    breakOverrideMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .nullable(),
    holidayWorkTreatment: z.enum(HOLIDAY_WORK_TREATMENTS).nullable(),
    taskNote: z.string().max(2000),
    siteName: z.string().max(200),
    remarks: z.string().max(1000),
});
/** 日報の保存（下書き・提出で共通の本文） */
export const dailyReportUpsertSchema = dailyInputSchema.omit({ date: true, dayType: true }).extend({
    /** 提出まで行うか。false は下書き保存 */
    submit: z.boolean().default(false),
});
/** 月間一覧のまとめ保存 */
export const bulkDailyReportSchema = z.object({
    entries: z
        .array(dailyInputSchema.omit({ dayType: true }).partial().extend({
        date: dateSchema,
        /**
         * 読み込んだ時点の版番号。別の画面や端末で先に保存されていれば競合として弾く。
         * 新規入力の日は null。
         */
        revision: z.number().int().nullable().optional(),
    }))
        .min(1)
        .max(31),
});
export const approvalDecisionSchema = z.object({
    decision: z.enum(['approve', 'reject']),
    comment: z.string().max(1000).default(''),
});
export const monthlyCloseSchema = z.object({
    month: monthSchema,
});
export const approvalGroupUpsertSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().max(500).default(''),
    approverEmployeeId: z.number().int().positive().nullable(),
    memberEmployeeIds: z.array(z.number().int().positive()).default([]),
});
export const employeeUpsertSchema = z.object({
    employeeId: z.number().int().positive(),
    name: z.string().min(1).max(100),
    email: z.string().email(),
    department: z.string().max(100).default(''),
    role: z.enum(['staff', 'approver', 'admin']),
    approvalGroupId: z.string().uuid().nullable(),
    hireDate: dateSchema.nullable(),
    active: z.boolean().default(true),
    /**
     * 勤怠提出の対象外（役員など。GAS の「取込対象外ID」に相当）。
     * 対象外の利用者は未入力・未承認の数に含めず、月次確定の条件からも外れる。ログインや承認はできる
     */
    excludedFromAttendance: z.boolean().default(false),
});
export const payrollMasterUpsertSchema = z.object({
    baseSalary: z.number().int().min(0),
    positionAllowance: z.number().int().min(0),
    managerAllowance: z.number().int().min(0),
    fixedOvertimeAllowance: z.number().int().min(0),
    mealAllowanceUnit: z.number().int().min(0),
});
//# sourceMappingURL=schemas.js.map